import { Link } from "react-router-dom"

import { ResetBenefitStatus } from "@common-shared/config/coverage.config"
import { COMMON_COVERAGE_HISTORY_CONFIG } from "@common-shared/config/coverageHistory.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import type { CoverageCheckerMode } from "./checker.config"
import { GENERAL_CONFIG } from "./general.config"
import { ROUTES_CONFIG } from "./routes.config"

export const TOOLTIPS_CONFIG = {
    auth: {
        registerEmailCollectionNote:
            "Your email will be used to create your account and for communication purposes. We will never market to your clients, and you can unsubscribe at any time.",
    },
    checker: {
        sessionCharge:
            "If you’re contracted with the payer selected below, please enter your contracted rate. If you’re not contracted, please enter your cash-pay rate.",
        payer: "Please select the closest match to the name of the payer on insurance card.",
        firstName: "Make sure that first name matches insurance card",
        lastName: "Make sure that last name matches insurance card",
        patientId:
            "In order to write check results to EHR later, you must provide the EHR Patient ID",
        memberId:
            "If the member ID isn’t available, Nirvana can try to locate the policy",
        zipCode:
            "Providing the patient ZIP significantly increases the likelihood of returning correct information",
        csvChecksWontRun:
            "Any checks with errors will not be included in the run.",
    },
    reports: {
        cantExportResultsText: {
            title: "Why can't I export?",
            text: `Exports are limited to ${COMMON_COVERAGE_HISTORY_CONFIG.maxReportCsvExportChecks.toLocaleString()} checks. Please narrow your filters or date range and try again.`,
        },
        visualizations: {
            get resetBenefitsStatus(): Record<ResetBenefitStatus, string> {
                return {
                    [ResetBenefitStatus.DETECTED_ACTION_REQUIRED]:
                        "We detected policy and benefits for next plan year with the same payer, but something has changed.",
                    [ResetBenefitStatus.DETECTED_NO_ACTION_REQUIRED]:
                        "We detected policy and benefits for next plan year with the same payer, and it matches current plan year.",
                    [ResetBenefitStatus.NOT_DETECTED]:
                        "We attempted but didn’t detect details for next plan year. The payer may not be returning plan details yet, or the patient may have changed coverage with a different payer.",
                }
            },
        },
    },
    coverageBreakdown: {
        activeAtRiskPlanStatus:
            "A plan status of Active - At Risk indicates that the client’s insurance plan is either delinquent or under investigation and there is a risk the Insurer will not provide coverage for services rendered. The client should reach out to their insurance company and resolve any outstanding issues.",
        activePartialCoverage:
            "This plan is Active, however Nirvana does not yet support cost estimates on this plan.",
        additionalPolicy:
            "Additional insurance plans associated with the patient, i.e. secondary plans.",
        medicareAdvantage:
            "Payer associated with detected Medicare Advantage policy",
    },
    bulkActions: {
        genericBulkAction:
            "Please note that only policies that are eligible for specific bulk action can be selected.",
        eligibleForBulkOverridePolicies:
            "Please note that only policies that are eligible for bulk overrides can be selected.",
        eligibleForBulkRecheck:
            "Please note that only policies that are eligible for re-run can be selected.",
    },
    singlePolicyView: {
        cantReRunCheckText: {
            title: "Why can’t I run a check?",
            // Made a function to get value and imported just type at top
            // Otherwise there's circular dependency
            text: (checkerMode: CoverageCheckerMode) => {
                return (
                    <PrimaryText
                        typography="h6"
                        centered
                        color={NIRVANA_COLORS.brand.white}
                    >
                        Please confirm that the payer and member information are
                        correct and{" "}
                        <Link
                            to={
                                ROUTES_CONFIG.coverageCheckerUrl +
                                `?${GENERAL_CONFIG.urlSearchParamsKeys.checkerMode}=${checkerMode}`
                            }
                        >
                            resubmit
                        </Link>
                        .
                    </PrimaryText>
                )
            },
        },

        cantWriteToEhrText: {
            title: "Why can't I write to EHR?",
            inactiveCoverage:
                "You can only write successfully recovered policy details.",
            notSupportedCheckType:
                "You can only write checks of type Standard and Medicaid.",
            missingPatientId:
                "Please run a check with a provided EHR Patient ID",
        },
    },
}
